<template>
    <Btn
        v-tooltip="enabled ? (isActive ? $t('common:tooltip.unlike') : $t('common:tooltip.like')) : undefined"
        :class="['protected-auth']"
        variant="outlined"
        active-color="green-500"
        v-bind="$attrs"
        :left-icon="currentIcon"
        :active="isActive"
        :label="likeCounter ? `${likeCounter}` : undefined"
        @click="onClick"
    />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
// eslint-disable-next-line import/no-restricted-paths
import { backend } from '@/api/backend';
import type { SuccessResponse } from '@/api/types';

interface FlashcardVoteResponse extends SuccessResponse {
    vote: {
        amount_votes: number;
        has_changed: boolean;
        has_voted: true;
        rating: number;
        vote_type: string;
    };
}

interface VoteResponse {
    downvotes: number;
    hasvotes: boolean;
    rating: number;
    upvotes: number;
}

/**
 * Button to signal a like for certain content.
 * By providing a request url, the user can change the like state & count when the button is enabled.
 */
export default defineComponent({
    name: 'BtnLike',
    inheritAttrs: false,
    props: {
        /**
         * Indicates whether the user activated a like already
         */
        active: {
            type: Boolean,
            default: false,
        },
        /**
         * Enables liking logic on the button on click
         */
        enabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Count of likes
         */
        likes: {
            type: Number,
            required: true,
        },
        /**
         * Url to call when user changes the like
         */
        requestUrl: {
            type: String,
            default: null,
        },
    },
    emits: [
        'update:likes',
        'update:active',
    ],
    data() {
        return {
            isActive: this.$props.active,
            likeCounter: this.$props.likes,
        };
    },
    computed: {
        ...mapGetters('auth', ['isLoggedIn']),
        currentIcon(): string {
            return this.isActive ? 'heart-full' : 'heart';
        },
    },
    methods: {
        async onClick() {
            if (!this.enabled || !this.isLoggedIn) return;

            if (this.requestUrl) {
                await backend.post<VoteResponse | FlashcardVoteResponse>(this.requestUrl).then((response) => {
                    if ('upvotes' in response.data) {
                        this.likeCounter = response.data.upvotes;
                    } else {
                        this.likeCounter = response?.data?.vote.rating ?? this.likeCounter;
                    }
                });
            } else {
                this.likeCounter = this.isActive ? this.likeCounter - 1 : this.likeCounter + 1;
            }

            this.isActive = !this.isActive;

            this.$emit('update:active');
            this.$emit('update:likes');
        },
    },
});
</script>
